import React from 'react'
import { SEO } from 'newComponents'
import Layout from '../components/layout'
import ClosingCTAs from '../components/ClosingCTAs'

import appServer from '../images/how-it-works/apptoserver.svg'
import architecture from '../images/how-it-works/architecture_wide.svg'
import updates from '../images/how-it-works/fast-updates.svg'
import resiliency from '../images/how-it-works/resiliency.svg'

const HowItWorks = () => (
  <Layout>
    <SEO
      title="How Feature Management Works"
      description="LaunchDarkly's feature management platform securely manages your software features, has a streaming architecture that serves flag changes in milliseconds, and uses a global CDN to ensure low latency."
    />
    <section className="section is-large bg-dark with-stars">
      <div className="container content">
        <div className="columns is-centered">
          <div className="column">
            <h1 className="title" style={{ paddingTop: 0 }}>
              Built for Speed and Reliability
            </h1>
            <p className="text-large">
              LaunchDarkly is built from the ground up to securely manage your software features. We are independently
              audited, and SOC 2 Type II certified. A global CDN ensures the lowest latency without remote requests. And
              our streaming architecture serves flag changes in milliseconds.
            </p>
          </div>
          <div className="column has-text-centered-tablet">
            <img src={appServer} style={{ width: '15rem', height: 'auto' }} alt="app to server" />
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="columns is-centered">
          <div className="column is-10">
            <div className="section-title">
              <h2 className="title">We Push Changes When They Happen</h2>
              <p className="subtitle">
                We use server-sent events (SSE), a protocol for one-way real-time messaging, to send messages to your
                servers whenever you change feature flag rules on your dashboard. The SSE connection is handled
                automatically by our SDKs.
              </p>
            </div>
            <div className="has-text-centered-tablet">
              <img src={architecture} alt="architecture" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <div className="clipped-container">
      <section className="section is-large bg-dark bg-static-stars is-clipped">
        <div className="container content">
          <div className="columns">
            <div className="column is-half">
              <h2 className="title">Real-Time Performance</h2>
              <p>
                Our unique streaming architecture means you can instantly turn features on/off when you need to without
                introducing any latency to your site. It only takes 200 milliseconds to propagate flag updates.
                <br />
                <br />
                Whether you are rolling out new features to your end users, testing in production, or hitting the kill
                switch—you have instant control over what your users experience.
              </p>
            </div>
            <div className="column is-half has-text-centered-tablet">
              <img src={updates} style={{ width: '26rem' }} alt="200ms to propagate flag updates" />
            </div>
          </div>
        </div>
      </section>
    </div>
    <section className="section bg-gradient is-large">
      <div className="container content">
        <div className="columns">
          <div className="column">
            <img src={resiliency} style={{ width: 'auto', height: '23rem' }} alt="resiliency" />
          </div>
          <div className="column">
            <h2 className="title">A Resilient Solution</h2>
            <p>
              A feature flag will always be available—all flags are stored locally and backed up by our globally
              distributed CDN provider. Even if the CDN goes down (we use Fastly, a widely-used CDN provider), your
              servers will continue operating with the last set of feature flag rules, so your customers will continue
              seeing the right set of features.
            </p>
            <p>
              The LaunchDarkly Relay Proxy establishes a connection to the LaunchDarkly streaming API, then proxies that
              stream connection to multiple clients. The relay proxy lets a number of servers connect to a local stream
              instead of making a large number of outbound connections to stream.launchdarkly.com. The relay proxy can
              be configured to proxy multiple environment streams, even across multiple projects.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Built for Scale</h2>
          <p className="subtitle">We can handle your biggest requests.</p>
        </div>
        <div className="columns is-mobile is-multiline is-centered cards">
          <div className="column is-narrow">
            <div className="info-card">
              <div className="focus">6 Trillion</div>
              <div className="focus">Feature Flags</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">Served</div>
              <div className="info">to users daily.</div>
            </div>
          </div>
          <div className="column is-narrow">
            <div className="info-card" style={{ paddingTop: '4rem' }}>
              <div className="focus">14 Million</div>
              <div className="focus">Mobile Devices</div>
              <div className="focus">&amp; Browsers</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">Served</div>
              <div className="info">world-wide daily.</div>
            </div>
          </div>
          <div className="column is-narrow">
            <div className="info-card">
              <div className="focus">3 Million</div>
              <div className="focus">Servers</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">Reached</div>
              <div className="info">world-wide daily.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ClosingCTAs />
  </Layout>
)

export default HowItWorks
